import { Grid, Tooltip, useMediaQuery, useTheme } from "@material-ui/core";
import { ArrowDownward, AssignmentTurnedIn, Block } from "@material-ui/icons";
import CommonButton from "app/common/Component/Button/CommonButton";
import PopupForm from "app/common/Component/Popup/PopupForm";
import FormikFocusError from "app/common/form/FormikFocusError";
import { observer } from "mobx-react";
import React, { forwardRef, memo } from "react";

import Config from "app/appConfig";
import ConfirmPopup from "app/common/Component/Popup/ConfirmPopup";
import GlobitsCheckBox from "app/common/form/GlobitsCheckBox";
import GlobitsDateTimePicker from "app/common/form/GlobitsDateTimePicker";
import GlobitsNumberInput from "app/common/form/GlobitsNumberInput";
import GlobitsPagingAutocomplete from "app/common/form/GlobitsPagingAutocomplete";
import GlobitsTextField from "app/common/form/GlobitsTextField";
import IconCrown from "app/common/Icon/IconCrown";
import IconDelete from "app/common/Icon/IconDelete";
import { PathModule, RANK_LIST } from "app/common/LocalConst";
import {
  getAge,
  getDepartmentsUser,
  getGender,
  getPositionCodesTreatment,
  isAdmin,
  transformDate,
  getDateTime,
} from "app/common/LocalFunction";
import { renderOptionsStaff } from "app/PatientManagement/ReportSalesAllocation/ReportSalesAllocationSearch";
import { pagingDepartment } from "app/services/DepartmentService";
import { pagingHealthCareRoom } from "app/services/HealthCareRoomService";
import { pagingPosition } from "app/services/PositionService";
import { pagingStaff } from "app/services/StaffService";
import { useStore } from "app/stores";
import { FieldArray, FormikConsumer, getIn, useFormikContext } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import LockIcon from "@material-ui/icons/Lock";
import LockOpenIcon from "@material-ui/icons/LockOpen";
import moment from "moment";

function AllocationServiceInfoPopup(props) {
  const { handleRenewScreen, initialPatient } = props;
  const urlParams = new URLSearchParams(window.location.search);
  const patientId = urlParams.get("patientId");
  const {
    allocationServiceInfo,
    handleClose,
    handleUpdateServiceAllocationInfo,
  } = useStore().patientManagementStore.serviceRequestItemStore;

  const validationSchema = Yup.object({
    assigner: Yup.object().nullable().required("Đây là trường bắt buộc"),
    orderDate: Yup.date()
      .transform(function (_, originalValue) {
        if (!originalValue) return null;
        if (Array.isArray(originalValue) && originalValue.length >= 3) {
          return moment([
            originalValue[0],
            originalValue[1] - 1,
            originalValue[2],
            originalValue[3] || 0,
            originalValue[4] || 0,
            originalValue[5] || 0,
          ]).toDate();
        }
        return moment(originalValue).isValid()
          ? moment(originalValue).toDate()
          : originalValue;
      })
      .typeError("Ngày chỉ định không đúng định dạng!")
      .nullable()
      .test(
        "is-greater-or-equal",
        "Ngày chỉ định phải lớn hơn hoặc bằng ngày đăng ký",
        function (value) {
          if (!value) return true;
          const { psrOrderDate } = this.parent;
          if (!psrOrderDate) return true;

          let regDate;
          if (Array.isArray(psrOrderDate) && psrOrderDate.length >= 3) {
            regDate = moment([
              psrOrderDate[0],
              psrOrderDate[1] - 1,
              psrOrderDate[2],
              psrOrderDate[3] || 0,
              psrOrderDate[4] || 0,
              psrOrderDate[5] || 0,
            ]);
          } else {
            regDate = moment(psrOrderDate);
          }

          if (regDate.isValid()) {
            let orderDate = moment(value);
            return orderDate.isSameOrAfter(regDate, "minute");
          }

          return true;
        },
      ),

    // room: Yup.object().nullable().required("Đây là trường bắt buộc"),
    encounterParticipants: Yup.array().test(
      "unique-staff",
      "Nhân viên không được trùng nhau",
      (list) => {
        if (!list) return true;
        const staffIds = list
          .filter((item) => item?.staff?.id)
          .map((item) => item.staff.id);
        return new Set(staffIds).size === staffIds.length;
      },
    ),
  });

  async function handleSubmitForm(values) {
    try {
      const response = await handleUpdateServiceAllocationInfo(values);
      if (response?.responseStatus !== false) {
        if (response?.id) await handleRenewScreen();
      }
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <PopupForm
      open={allocationServiceInfo?.id ? true : false}
      size="lg"
      handleClose={handleClose}
      title={`Cập nhật thông tin chỉ định`}
      formik={{
        initialValues: {
          ...allocationServiceInfo,
          patientId: patientId || allocationServiceInfo?.encounter?.patientId,
        },
        onSubmit: handleSubmitForm,
        enableReinitialize: true,
        validationSchema: validationSchema,
      }}
      action={({ values, isSubmitting }) => {
        return (
          <FormikConsumer>
            {(formik) => (
              <div className="flex-center gap-1">
                <CommonButton className="btn-gray" onClick={handleClose}>
                  <Block className="mr-1" /> Đóng
                </CommonButton>

                <CommonButton
                  className="btn-add"
                  type="submit"
                  disabled={isSubmitting}
                >
                  <AssignmentTurnedIn className="mr-1" /> Cập nhật chỉ định
                </CommonButton>
              </div>
            )}
          </FormikConsumer>
        );
      }}
    >
      {({ setFieldValue, values }) => {
        return (
          <React.Fragment>
            <FormikFocusError />

            <AllocationServiceInfoPopupContent />
            <DialogError
              patient={initialPatient?.patientInfo}
              setFieldValue={setFieldValue}
              values={values}
            />
          </React.Fragment>
        );
      }}
    </PopupForm>
  );
}

export default memo(observer(AllocationServiceInfoPopup));

const AllocationServiceInfoPopupContent = observer(function (props) {
  let POSITION_CODES_TREATMENT = getPositionCodesTreatment();
  let isAdminPermission = isAdmin();
  const departmentsUser = getDepartmentsUser();

  const { values, setFieldValue } = useFormikContext();

  return (
    <div className="p-2">
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <PatientInfoAllocationServiceBrief
            patient={values?.encounter?.patient}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={3}>
          <GlobitsDateTimePicker
            label="Thời gian chỉ định"
            name="orderDate"
            isDateTimePicker
            requiredLabel
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={3}>
          <GlobitsPagingAutocomplete
            api={pagingStaff}
            name="assigner"
            requiredLabel
            displayLabel="staffName"
            label="Người chỉ định"
            searchObject={{ isActive: true }}
            getOptionDisabled={(option) => {
              const staffList = getIn(values, "encounterParticipants");
              return staffList?.some((it) => it?.staff?.id === option?.id);
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={3}>
          <GlobitsPagingAutocomplete
            api={
              isAdminPermission
                ? pagingDepartment
                : async () => ({
                    data: {
                      content: departmentsUser || [],
                      totalElements: departmentsUser?.length || 0,
                      totalPages: 1,
                    },
                  })
            }
            name="department"
            label="Cơ sở"
            onChange={(_, value) => {
              setFieldValue(`department`, value);
              setFieldValue(`room`, null);
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={3}>
          <GlobitsPagingAutocomplete
            api={pagingHealthCareRoom}
            name="room"
            label="Phòng"
            // required
            searchObject={{
              departmentId: values?.department?.id,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <GlobitsNumberInput
            label="Số cc "
            roundNumberToFixed={2}
            decimal
            name={"currentVolumeTreated"}
            disabled
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={3}>
          <div className="flex align-center" style={{ marginTop: 16 }}>
            <input
              type="checkbox"
              checked={!!values?.isOutsideDepartment}
              onChange={(e) =>
                setFieldValue("isOutsideDepartment", e.target.checked)
              }
              style={{ marginRight: 8 }}
            />
            <span>Làm ngoài cơ sở</span>
          </div>
        </Grid>

        <Grid item xs={12} sm={6}>
          <GlobitsTextField name="note" label="Ghi chú" notDelay isTextArea />
        </Grid>
        <Grid item xs={12}>
          <EncounterParticipantsTable
            POSITION_CODES_TREATMENT={POSITION_CODES_TREATMENT}
          />
        </Grid>
      </Grid>
    </div>
  );
});

const PatientInfoAllocationServiceBrief = observer(function (props) {
  const { patient } = props;

  const { values, setFieldValue } = useFormikContext();

  const onViewServiceRequestItem = { ...values };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

  let color_rank =
    RANK_LIST.find((el) => patient?.currentPatientRank?.code === el?.code)
      ?.color || "#219d77";

  return (
    <div
      className="px-2"
      style={{
        border: "1px dash #ccc ",
      }}
    >
      <Grid
        container
        spacing={1}
        className={`flex align-center ${isMobile && "flex-center"} mb-2 `}
        style={{
          border: "1px dashed #ccc",
          borderRadius: "4px",
        }}
      >
        <Grid item xs={12} className="justify-center">
          <h4
            className={` py-1 patient-name text-second flex align-center general-rank w-100 text-center`}
          >
            <span className="uppercase w-100">
              {`${patient?.displayName}`}{" "}
              {patient?.gender ? ` - ${getGender(patient?.gender)} ` : ""}
              {patient?.birthDate ? `- ${getAge(patient?.birthDate)}` : ""}
              {patient?.code ? `- ${patient?.code}` : ""}
            </span>

            <div className="flex align-center mb-1">
              <Tooltip title="Hạng khách hàng">
                <div>
                  <IconCrown width={44} color={color_rank} />
                </div>
              </Tooltip>
              <span style={{ color: color_rank }}>
                {patient?.currentPatientRank
                  ? ` - ${patient?.currentPatientRank?.name}`
                  : ""}
              </span>
            </div>
          </h4>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={1} className="flex-between service-info-box">
            <Grid item xs={12} md={4}>
              <span className="info-item info-service w-100 text-center">
                💉 <strong>Dịch vụ:</strong>
                {onViewServiceRequestItem?.service?.tradeName ||
                  onViewServiceRequestItem?.service?.name}{" "}
                ({onViewServiceRequestItem?.service?.code})
              </span>
            </Grid>

            <Grid item xs={12} md={4}>
              <span className="info-item info-service w-100 text-center">
                🗓️ <strong>Ngày đăng ký:</strong>{" "}
                {getDateTime(onViewServiceRequestItem?.psrOrderDate)}
              </span>
            </Grid>

            <Grid item xs={12} md={4}>
              <span className="info-item info-room">
                🏢 <strong>Phòng đã chỉ định:</strong>{" "}
                {onViewServiceRequestItem?.room?.code} -{" "}
                {onViewServiceRequestItem?.room?.name}
              </span>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
});

const EncounterParticipantsTable = observer((props) => {
  const { POSITION_CODES_TREATMENT } = props;

  const { values, setFieldValue } = useFormikContext();

  return (
    <>
      <FieldArray name={`encounterParticipants`}>
        {({ remove, push }) => {
          let encounterParticipantsItems = getIn(
            values,
            `encounterParticipants`,
          );

          return (
            <>
              <Grid item xs={12}>
                <Grid container spacing={1} className="align-end">
                  <Grid item xs={12} sm={6} md={2}>
                    <p className="table-section-title">Chia tour</p>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <GlobitsPagingAutocomplete
                      label={"Chọn vai trò"}
                      name={"position"}
                      api={pagingPosition}
                      searchObject={{
                        isUsing: true,
                        positionCodes: POSITION_CODES_TREATMENT,
                      }}
                      onChange={(_, value) => {
                        setFieldValue("position", value);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <GlobitsPagingAutocomplete
                      label={"Nhân viên"}
                      displayLabel={"staffName"}
                      api={pagingStaff}
                      searchObject={{
                        positionId: getIn(values, "position")?.id || "",
                        departmentId: getIn(values, "department")?.id || "",
                        isActive: true,
                      }}
                      name={"staff"}
                      getOptionDisabled={(option) => {
                        const staffList = getIn(
                          values,
                          `encounterParticipants`,
                        );
                        if (
                          staffList?.find((it) => it?.staff?.id === option?.id)
                        ) {
                          return true;
                        }
                        return false;
                      }}
                      onChange={(_, value) => {
                        setFieldValue("staff", value);
                        // if (value?.position) {
                        //   setFieldValue("position", value.position);
                        // }
                      }}
                      ListboxComponent={forwardRef((props, ref) => {
                        const { children, ...other } = props;
                        return (
                          <ul {...other} ref={ref}>
                            <ol>
                              <li>{children}</li>
                            </ol>
                          </ul>
                        );
                      })}
                      renderOption={(option) => renderOptionsStaff(option)}
                    />
                  </Grid>

                  <Grid item xs={2} className="flex align-end">
                    <CommonButton
                      onClick={() => {
                        const staffList =
                          getIn(values, "encounterParticipants") || [];
                        if (
                          values?.staff?.id &&
                          staffList.some(
                            (it) => it?.staff?.id === values?.staff?.id,
                          )
                        ) {
                          toast.warning(
                            "Nhân viên này đã được thêm vào danh sách",
                          );
                          return;
                        }
                        push({
                          staff: values?.staff,
                          position: values?.position
                            ? values?.position
                            : values?.staff?.position,
                        });
                        setFieldValue("position", null);
                        setFieldValue("staff", null);
                      }}
                      disabled={
                        values?.staff &&
                        (getIn(values, "encounterParticipants") || []).some(
                          (it) => it?.staff?.id === values?.staff?.id,
                        )
                      }
                    >
                      <ArrowDownward />
                    </CommonButton>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <div className="table-root mt-2">
                  <div style={{ minWidth: "420px" }}>
                    <table className="table-container">
                      <thead>
                        <tr className="row-table-header">
                          <th width="5%">STT</th>
                          <th width="10%">Thao tác</th>
                          <th width="30%">Vai trò</th>
                          <th width="30%">Nhân viên</th>
                          <th width="20%">Người thực hiện chính</th>
                          <th width="20%">Trạng thái</th>
                        </tr>
                      </thead>
                      <tbody>
                        {encounterParticipantsItems?.length > 0 &&
                          encounterParticipantsItems?.map((parti, ind) => {
                            let nameSpaceParticipants = `encounterParticipants[${ind}]`;
                            const isLocked = parti?.locked;
                            return (
                              <tr
                                key={ind}
                                className={`${parti?.id ? "old-staff" : ""}`}
                              >
                                <td align="center">{ind + 1}</td>
                                <td align="center">
                                  <span
                                    tooltip={"Xóa"}
                                    className={`pointer tooltip ${isLocked ? "disabled" : ""}`}
                                    onClick={() => remove(ind)}
                                  >
                                    <IconDelete />
                                  </span>
                                </td>
                                <td>
                                  <GlobitsPagingAutocomplete
                                    api={pagingPosition}
                                    name={`${nameSpaceParticipants}.position`}
                                    autoUpercase
                                    searchObject={{
                                      isUsing: true,
                                      positionCodes: POSITION_CODES_TREATMENT,
                                    }}
                                    handleChange={function (_, value) {
                                      setFieldValue(
                                        `${nameSpaceParticipants}.position`,
                                        value,
                                      );
                                      setFieldValue(
                                        `${nameSpaceParticipants}.staff`,
                                        null,
                                      );
                                    }}
                                    disabled={isLocked}
                                  />
                                </td>
                                <td>
                                  <GlobitsPagingAutocomplete
                                    api={pagingStaff}
                                    name={`${nameSpaceParticipants}.staff`}
                                    displayLabel={"staffName"}
                                    autoUpercase
                                    searchObject={{
                                      positionId:
                                        getIn(
                                          values,
                                          `${nameSpaceParticipants}.position.id`,
                                        ) || "",
                                      departmentId:
                                        getIn(values, "department")?.id || "",
                                      isActive: true,
                                    }}
                                    disabled={isLocked}
                                    getOptionDisabled={(option) => {
                                      const staffList =
                                        getIn(
                                          values,
                                          "encounterParticipants",
                                        ) || [];
                                      return staffList.some(
                                        (it, i) =>
                                          i !== ind &&
                                          it?.staff?.id === option?.id,
                                      );
                                    }}
                                    ListboxComponent={forwardRef(
                                      (props, ref) => {
                                        const { children, ...other } = props;
                                        return (
                                          <ul {...other} ref={ref}>
                                            <ol>
                                              <li>{children}</li>
                                            </ol>
                                          </ul>
                                        );
                                      },
                                    )}
                                    renderOption={(option) =>
                                      renderOptionsStaff(option)
                                    }
                                  />
                                </td>

                                <td>
                                  <GlobitsCheckBox
                                    name={`${nameSpaceParticipants}.isMain`}
                                    alignPosition="center"
                                    onChange={(e) => {
                                      const isMain = e.target.checked;
                                      setFieldValue(
                                        `${nameSpaceParticipants}.isMain`,
                                        isMain,
                                      );
                                    }}
                                    disabled={isLocked}
                                  />
                                </td>
                                <td align="center">
                                  {isLocked ? (
                                    <LockIcon
                                      style={{ color: "#888" }}
                                      fontSize="small"
                                    />
                                  ) : (
                                    <LockOpenIcon
                                      style={{ color: "#4caf50" }}
                                      fontSize="small"
                                    />
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Grid>
            </>
          );
        }}
      </FieldArray>
    </>
  );
});

const DialogError = observer(({ patient, values, setFieldValue }) => {
  const { openConfirmMessage, permissionErrorMessage, handleClose } =
    useStore().patientManagementStore.serviceRequestItemStore;
  return (
    <>
      {permissionErrorMessage ? (
        <ConfirmPopup
          open={!!permissionErrorMessage}
          onClose={() => {
            handleClose();
          }}
          text={permissionErrorMessage}
          textClose={"Đóng"}
          title="Thông báo"
          isNotAPromise
          hideConfirm
        />
      ) : (
        !!openConfirmMessage && (
          <ConfirmPopup
            open={!!openConfirmMessage}
            onClose={() => {
              handleClose();
              // khi đóng popup thì reset lại ngày đăng kí
              setFieldValue("orderDate", values?.orderDate);
            }}
            text={openConfirmMessage}
            onConfirm={() => {
              const url =
                Config.ROOT_PATH +
                PathModule.PATIENT_MANAGEMENT +
                `/patient-general?patientId=${patient?.id}`;

              window.open(url, "_blank");
            }}
            textConfirm={"Thông tin khách"}
            textClose={"Quay lại"}
            title="Thao tác thất bại"
            isNotAPromise
          />
        )
      )}
    </>
  );
});
